import { supabase } from '../lib/supabase';

/**
 * Calculation Service - Data layer for managing calculator results
 * 
 * This service provides CRUD operations for the calculations table in Supabase.
 * All operations are automatically scoped to the authenticated user via RLS policies.
 * 
 * Database Schema:
 * - id: UUID primary key
 * - user_id: UUID foreign key to auth.users
 * - created_at, updated_at: Timestamps
 * - calculation_type: TEXT - type of calculation (e.g., 'Retirement', 'OpportunityCost', 'Interest', 'LTV')
 * - calculation_data: JSONB - flexible storage for all calculation inputs and results
 */

/**
 * Creates a new calculation record for the authenticated user
 * 
 * @param {Object} calcData - The calculation data to save
 * @param {string} calcData.calculation_type - Type of calculation (e.g., 'Retirement', 'OpportunityCost', 'Interest', 'LTV')
 * @param {Object} calcData.calculation_data - Object containing all inputs and results from the calculator
 * @param {Object} [calcData.calculation_data.inputs] - Input parameters used for the calculation
 * @param {Object} [calcData.calculation_data.results] - Calculation results
 * @param {Array} [calcData.calculation_data.chartData] - Chart data for visualizations
 * @param {...*} [calcData.calculation_data.additionalProps] - Any other calculator-specific data
 * 
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 *   - data: The created calculation object if successful
 *   - error: Error object if operation failed
 */
async function createCalculation(calcData) {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session) {
      return { data: null, error: new Error('No authenticated user found. Please sign in to save calculations.') };
    }
    
    // Validate required fields
    if (!calcData.calculation_type) {
      return { data: null, error: new Error('Calculation type is required') };
    }
    
    if (!calcData.calculation_data) {
      return { data: null, error: new Error('Calculation data is required') };
    }
    
    // Prepare the data for insertion
    const insertData = {
      user_id: session.user.id,
      calculation_type: calcData.calculation_type,
      calculation_data: calcData.calculation_data
    };
    
    // Insert the new calculation
    const { data, error } = await supabase
      .from('calculations')
      .insert([insertData])
      .select()
      .single();
    
    if (error) {
      console.error('Error creating calculation:', error);
      return { data: null, error: new Error(`Failed to save calculation: ${error.message}`) };
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Unexpected error in createCalculation:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Retrieves calculations for the authenticated user
 * 
 * @param {string} [type] - Optional calculation type to filter by (e.g., 'Retirement', 'OpportunityCost')
 *                          If not provided, returns all calculations for the user
 * 
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 *   - data: Array of calculation objects if successful
 *   - error: Error object if operation failed
 */
async function getCalculations(type) {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session) {
      return { data: null, error: new Error('No authenticated user found. Please sign in to view calculations.') };
    }
    
    // Build the query
    let query = supabase
      .from('calculations')
      .select('*')
      .eq('user_id', session.user.id);
    
    // Add type filter if provided
    if (type) {
      query = query.eq('calculation_type', type);
    }
    
    // Execute query with ordering
    const { data, error } = await query
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching calculations:', error);
      return { data: null, error: new Error(`Failed to fetch calculations: ${error.message}`) };
    }
    
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Unexpected error in getCalculations:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Deletes a calculation record by ID
 * 
 * @param {string} calculationId - UUID of the calculation to delete
 * 
 * @returns {Promise<{error: Error|null}>}
 *   - error: Error object if operation failed, null if successful
 */
async function deleteCalculation(calculationId) {
  try {
    // Validate input
    if (!calculationId) {
      return { error: new Error('Calculation ID is required') };
    }
    
    // Get current user session for security
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session) {
      return { error: new Error('No authenticated user found. Please sign in to delete calculations.') };
    }
    
    // Delete the calculation
    // RLS policies will ensure users can only delete their own calculations
    const { error } = await supabase
      .from('calculations')
      .delete()
      .eq('id', calculationId);
    
    if (error) {
      console.error('Error deleting calculation:', error);
      return { error: new Error(`Failed to delete calculation: ${error.message}`) };
    }
    
    return { error: null };
  } catch (error) {
    console.error('Unexpected error in deleteCalculation:', error);
    return { error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Updates an existing calculation record
 * 
 * @param {string} calculationId - UUID of the calculation to update
 * @param {Object} updateData - The data to update
 * @param {Object} updateData.calculation_data - Updated calculation data
 * 
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 *   - data: The updated calculation object if successful
 *   - error: Error object if operation failed
 */
async function updateCalculation(calculationId, updateData) {
  try {
    // Validate input
    if (!calculationId) {
      return { data: null, error: new Error('Calculation ID is required') };
    }
    
    if (!updateData.calculation_data) {
      return { data: null, error: new Error('Calculation data is required') };
    }
    
    // Get current user session for security
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session) {
      return { data: null, error: new Error('No authenticated user found. Please sign in to update calculations.') };
    }
    
    // Update the calculation
    // RLS policies will ensure users can only update their own calculations
    const { data, error } = await supabase
      .from('calculations')
      .update({
        calculation_data: updateData.calculation_data,
        updated_at: new Date().toISOString()
      })
      .eq('id', calculationId)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating calculation:', error);
      return { data: null, error: new Error(`Failed to update calculation: ${error.message}`) };
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Unexpected error in updateCalculation:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

// Export the service object with all available methods
export const calculationService = {
  createCalculation,
  getCalculations,
  deleteCalculation,
  updateCalculation
};

// Also export individual functions for direct imports if needed
export {
  createCalculation,
  getCalculations,
  deleteCalculation,
  updateCalculation
};